import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Check, X, Shield, Loader2, RotateCcw } from "lucide-react";
import { useVerification } from "@/hooks/useVerification";
import { toast } from "@/hooks/use-toast";

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const VerificationModal = ({ isOpen, onClose, onSuccess }: VerificationModalProps) => {
  const { status, error, confidence, verifyWithSelfie, reset, isVerified } = useVerification();
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please allow camera permissions.",
        variant: "destructive"
      });
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  // Capture photo
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      // Mirror the image (selfie style)
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);
    }

    const imageData = canvas.toDataURL("image/jpeg", 0.9);
    setCapturedImage(imageData);
    stopCamera();
  }, [stopCamera]);

  // Retake photo
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    reset();
    startCamera();
  }, [reset, startCamera]);

  // Submit for verification
  const submitVerification = useCallback(async () => {
    if (!capturedImage) return;

    // Convert base64 to File
    const response = await fetch(capturedImage);
    const blob = await response.blob();
    const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

    const result = await verifyWithSelfie(file);

    if (result.success) {
      toast({
        title: "🎉 Verified!",
        description: "Your profile is now verified. Enjoy the verified badge!"
      });
      onSuccess();
      handleClose();
    } else {
      toast({
        title: "Verification Failed",
        description: result.message,
        variant: "destructive"
      });
    }
  }, [capturedImage, verifyWithSelfie, onSuccess]);

  // Clean up on close
  const handleClose = useCallback(() => {
    stopCamera();
    setCapturedImage(null);
    reset();
    onClose();
  }, [stopCamera, reset, onClose]);

  // Auto-start camera when modal opens
  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      startCamera();
    } else {
      handleClose();
    }
  }, [startCamera, handleClose]);

  if (isVerified) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Already Verified
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">You're verified!</h3>
            <p className="text-muted-foreground">
              Your profile has been verified. Other users can see your verified badge.
            </p>
          </div>
          <Button onClick={handleClose} className="w-full">Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Verify Your Profile
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Instructions */}
          <p className="text-sm text-muted-foreground">
            Take a selfie to verify you're the same person in your profile photos. 
            This earns you a verified badge that increases trust.
          </p>

          {/* Camera/Preview area */}
          <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-muted">
            {!capturedImage ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover transform scale-x-[-1]"
              />
            ) : (
              <img
                src={capturedImage}
                alt="Captured selfie"
                className="w-full h-full object-cover"
              />
            )}

            {/* Overlay guide */}
            {!capturedImage && stream && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 border-2 border-dashed border-primary/50 rounded-full" />
              </div>
            )}

            {/* Loading overlay */}
            {(status === "uploading" || status === "verifying") && (
              <div className="absolute inset-0 bg-background/80 flex flex-col items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-primary mb-2" />
                <p className="text-sm font-medium">
                  {status === "uploading" ? "Uploading..." : "Verifying..."}
                </p>
              </div>
            )}

            {/* Success overlay */}
            {status === "success" && (
              <div className="absolute inset-0 bg-primary/20 flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-2">
                  <Check className="w-8 h-8 text-primary-foreground" />
                </div>
                <p className="text-sm font-bold text-primary">Verified!</p>
                {confidence && (
                  <p className="text-xs text-muted-foreground">
                    {Math.round(confidence * 100)}% match
                  </p>
                )}
              </div>
            )}

            {/* Failed overlay */}
            {status === "failed" && (
              <div className="absolute inset-0 bg-destructive/20 flex flex-col items-center justify-center p-4">
                <div className="w-16 h-16 rounded-full bg-destructive flex items-center justify-center mb-2">
                  <X className="w-8 h-8 text-destructive-foreground" />
                </div>
                <p className="text-sm font-bold text-destructive">Verification Failed</p>
                <p className="text-xs text-muted-foreground text-center mt-1">
                  {error || "Please try again with a clearer photo"}
                </p>
              </div>
            )}
          </div>

          {/* Hidden canvas for capture */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Action buttons */}
          <div className="flex gap-3">
            {!capturedImage ? (
              <Button
                onClick={capturePhoto}
                disabled={!stream}
                className="flex-1 pop-gradient"
              >
                <Camera className="w-4 h-4 mr-2" />
                Take Selfie
              </Button>
            ) : status === "idle" ? (
              <>
                <Button
                  variant="outline"
                  onClick={retakePhoto}
                  className="flex-1"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retake
                </Button>
                <Button
                  onClick={submitVerification}
                  className="flex-1 pop-gradient"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Verify
                </Button>
              </>
            ) : status === "failed" ? (
              <Button
                onClick={retakePhoto}
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            ) : null}
          </div>

          {/* Tips */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Make sure your face is well-lit and clearly visible</p>
            <p>• Look directly at the camera</p>
            <p>• Match the pose/angle of your profile photo if possible</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationModal;